// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from "react";
import "./../styles/SideNavBar.css";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LayersIcon from "@mui/icons-material/Layers";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import { KeyboardArrowDown } from "@mui/icons-material";
import Slide from '@mui/material/Slide';
import { Box } from "@mui/material";
import AssessmentIcon from '@mui/icons-material/Assessment';
import InsightsIcon from '@mui/icons-material/Insights';

const drawerWidth = 200;

const openedMixin = (theme: Theme): CSSObject => ({
   width: drawerWidth,
   backgroundColor: "#212A30",
   transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
   }),
   overflowX: "hidden",
   marginTop: "70px",
});

const closedMixin = (theme: Theme): CSSObject => ({
   transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
   }),
   overflowX: "hidden",
   backgroundColor: "#212A30",
   marginTop: "70px",
   width: `calc(${theme.spacing(7)} + 1px)`,
   [theme.breakpoints.up("sm")]: {
      width: `calc(${theme.spacing(8)} + 1px)`,
   },
});

export const DrawerFooter = styled("div")(({ theme }) => ({
   position: "fixed",
   bottom: 0,
   display: "flex",
   alignItems: "center",
   justifyContent: "flex-end",
   padding: theme.spacing(0, 2),
   ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
   shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
   width: drawerWidth,
   flexShrink: 0,
   backgroundColor: "#212A30",
   whiteSpace: "nowrap",
   boxSizing: "border-box",
   ...(open && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": openedMixin(theme),
   }),
   ...(!open && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": closedMixin(theme),
   }),
}));

export const NavBar = () => {
   const [activeNav, setActiveNav] = useState<string>("cases");
   const location = useLocation();
   const [open, setOpen] = React.useState(false);
   const [dropAccounts, setDropAccounts] = useState(false);
   const containerRef = React.useRef<HTMLElement>(null);

   useEffect(() => {
      const path = location.pathname.slice(1); // remove the leading slash
      if (path.split("/")[1] == "accounts" && path.split("/")[3] == "projects") {
         setActiveNav("projects");
      } else if (path.split("/")[1] == "all-accounts") {
         setActiveNav("all-accounts");      
      } else if (path.split("/")[1] == "accounts") {
            setActiveNav("all-accounts");
      } else if (path.split("/")[1] == "my-accounts") {
         setActiveNav("my-accounts");
      } else if (path.split("/")[1] == "cases") {
         setActiveNav("cases");
      } else if (path.split("/")[1] == "projects") {
         setActiveNav("projects");
      } else if (path.split("/")[1] == "user-scan") {
         setActiveNav("user-scan");
      } else if (path.split("/")[1] == "customer-health") {
         setActiveNav("customer-health");
      } else if (path.split("/")[1] == "usage-metrics") {
         setActiveNav("usage-metrics");
      }
   }, [location]);

   const handleDropAccounts = () => {
      setDropAccounts(!dropAccounts);
   }

   return (
      <div className="nav" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
         <Drawer variant="permanent" open={open}>
            <List style={{ paddingTop: 0, paddingBottom: 0 }}>
               <Link
                  to="/support/cases"
                  style={{ textDecoration: "none", color: "white" }}
               >
                  <Tooltip title={!open ? "Cases" : ""} placement="right" arrow>
                     <div
                        className={`list-group-items ${activeNav === "cases" ? "active" : ""
                           }`}
                     >
                        <ListItem disablePadding sx={{ display: "block" }}>
                           <ListItemButton
                              sx={{
                                 minHeight: 48,
                                 justifyContent: open ? "initial" : "center",
                                 px: 2.5,
                              }}
                           >
                              <ListItemIcon
                                 sx={{
                                    minWidth: 0,
                                    mr: open ? 3 : "auto",
                                    justifyContent: "center",
                                 }}
                              >
                                 <LayersIcon
                                    className={`svg-icons ${activeNav === "cases" ? "active" : ""
                                       }`}
                                    sx={{
                                       color: "white",
                                    }}
                                 ></LayersIcon>
                              </ListItemIcon>
                              <ListItemText
                                 primary="Cases"
                                 sx={{ opacity: open ? 1 : 0 }}
                              />
                           </ListItemButton>
                        </ListItem>
                     </div>
                  </Tooltip>
               </Link>
            </List>
            <List style={{ paddingTop: 0, paddingBottom: 0 }} >
               <Tooltip title={!open ? "Accounts" : ""} placement="right" arrow>
                  <div
                     className={`list-group-items ${((activeNav == "all-accounts" || activeNav == "my-accounts") &&
                        (!dropAccounts || !open)) ? "active" : ""}`}
                     style={{ borderBottom: `${open && dropAccounts ? "none" : "1px solid rgba(229, 225, 225, 0.125)"}` }}
                  >
                     <ListItem disablePadding sx={{ display: "block" }} onClick={handleDropAccounts}>
                        <ListItemButton
                           sx={{
                              minHeight: 48,
                              justifyContent: open ? "initial" : "center",
                              px: 2.5,
                           }}
                        >
                           <ListItemIcon
                              sx={{
                                 minWidth: 0,
                                 mr: open ? 3 : "auto",
                                 justifyContent: "center",
                              }}
                           >
                              <AccountCircleIcon
                                 className={`svg-icons ${((activeNav == "all-accounts" || activeNav == "my-accounts")
                                    && (!dropAccounts || !open)) ? "active" : ""}`}
                                 sx={{
                                    color: "white",
                                 }}
                              />
                           </ListItemIcon>

                           <ListItemText
                              primary="Accounts"
                              sx={{ opacity: open ? 1 : 0 }}
                           />
                           {open && (dropAccounts ? <KeyboardArrowDown /> : <ChevronRightIcon />)}

                        </ListItemButton>
                     </ListItem>
                  </div>
               </Tooltip>
            </List>
            <Box ref={containerRef}>
               {open && dropAccounts &&
                  <Slide in={dropAccounts} direction="down" timeout={{ enter: 500 }} container={containerRef.current} >
                     <List style={{ paddingTop: 0, paddingBottom: 0 }}>
                        <Link
                           to="/support/my-accounts"
                           style={{ color: "white", textDecoration: "none" }}
                        >
                           <div
                              className={`list-group-sub-items ${activeNav === "my-accounts" ? "active" : ""
                                 }`}
                              style={{ borderBottom: "none" }}
                           >
                              <ListItem disablePadding sx={{ display: "block" }}>
                                 <ListItemButton
                                    sx={{
                                       minHeight: 48,
                                       justifyContent: open ? "initial" : "center",
                                       px: 2.5,
                                    }}
                                 >
                                    <ListItemText
                                       primary="My Accounts"
                                       style={{
                                          marginLeft: "3.5rem",
                                       }}
                                    />
                                 </ListItemButton>
                              </ListItem>
                           </div>
                        </Link>
                     </List>
                  </Slide>
               }
               {open && dropAccounts &&
                  <Slide in={dropAccounts} direction="down" timeout={{ enter: 500 }} container={containerRef.current} >
                     <List style={{ paddingTop: 0, paddingBottom: 0 }}>
                        <Link
                           to="/support/all-accounts"
                           style={{ textDecoration: "none", color: "white" }}
                        >
                           <div
                              className={`list-group-sub-items ${activeNav === "all-accounts" ? "active" : ""
                                 }`}
                           >
                              <ListItem disablePadding sx={{ display: "block" }}>
                                 <ListItemButton
                                    sx={{
                                       minHeight: 48,
                                       justifyContent: open ? "initial" : "center",
                                       px: 2.5,
                                    }}
                                 >
                                    <ListItemText
                                       primary="All Accounts"
                                       style={{
                                          marginLeft: "3.5rem",
                                       }}
                                    />
                                 </ListItemButton>
                              </ListItem>
                           </div>
                        </Link>
                     </List>
                  </Slide>
               }
            </Box>
            <List style={{ paddingTop: 0, paddingBottom: 0 }}>
               <Link
                  to="/support/projects"
                  style={{ textDecoration: "none", color: "white" }}
               >
                  <Tooltip title={!open ? "Projects" : ""} placement="right" arrow>
                     <div
                        className={`list-group-items ${activeNav === "projects" ? "active" : ""
                           }`}
                     >
                        <ListItem disablePadding sx={{ display: "block" }}>
                           <ListItemButton
                              sx={{
                                 minHeight: 48,
                                 justifyContent: open ? "initial" : "center",
                                 px: 2.5,
                              }}
                           >
                              <ListItemIcon
                                 sx={{
                                    minWidth: 0,
                                    mr: open ? 3 : "auto",
                                    justifyContent: "center",
                                 }}
                              >
                                 <ContactPageIcon
                                    className={`svg-icons ${activeNav === "projects" ? "active" : ""
                                       }`}
                                    sx={{
                                       color: "white",
                                    }}
                                 />
                              </ListItemIcon>
                              <ListItemText
                                 primary="Projects"
                                 sx={{ opacity: open ? 1 : 0 }}
                              />
                           </ListItemButton>
                        </ListItem>
                     </div>
                  </Tooltip>
               </Link>
            </List>
            <List style={{ paddingTop: 0, paddingBottom: 0 }}>
               <Link
                  to="/support/user-scan"
                  style={{ textDecoration: "none", color: "white" }}
               >
                  <Tooltip title={!open ? "User scan" : ""} placement="right" arrow>
                     <div
                        className={`list-group-items ${activeNav === "user-scan" ? "active" : ""
                           }`}
                     >
                        <ListItem disablePadding sx={{ display: "block" }}>
                           <ListItemButton
                              sx={{
                                 minHeight: 48,
                                 justifyContent: open ? "initial" : "center",
                                 px: 2.5,
                              }}
                           >
                              <ListItemIcon
                                 sx={{
                                    minWidth: 0,
                                    mr: open ? 3 : "auto",
                                    justifyContent: "center",
                                 }}
                              >
                                 <PersonSearchIcon
                                    className={`svg-icons ${activeNav === "user-scan" ? "active" : ""
                                       }`}
                                    sx={{
                                       color: "white",
                                    }}
                                 />
                              </ListItemIcon>
                              <ListItemText
                                 primary="User scan"
                                 sx={{ opacity: open ? 1 : 0 }}
                              />
                           </ListItemButton>
                        </ListItem>
                     </div>
                  </Tooltip>
               </Link>
            </List>
            <List style={{ paddingTop: 0, paddingBottom: 0 }}>
               <Link
                  to="/support/customer-health" 
                  style={{ textDecoration: "none", color: "white" }}
               >
                  <Tooltip title={!open ? "Customer Health Analysis" : ""} placement="right" arrow>
                    
                     <div
                        className={`list-group-items ${activeNav === "customer-health" ? "active" : ""
                           }`}
                     >
                        <ListItem disablePadding sx={{ display: "block" }}>
                           <ListItemButton
                              sx={{
                                 minHeight: 48,
                                 justifyContent: open ? "initial" : "center",
                                 px: 2.5,
                              }}
                           >
                              <ListItemIcon
                                 sx={{
                                    minWidth: 0,
                                    mr: open ? 3 : "auto",
                                    justifyContent: "center",
                                 }}
                              >
                                
                                 <AssessmentIcon
                                    className={`svg-icons ${activeNav === "customer-health" ? "active" : ""
                                       }`}
                                    sx={{
                                       color: "white",
                                    }}
                                 />
                              </ListItemIcon>
                              <ListItemText
                                 primary="Customer Health" 
                                 sx={{ opacity: open ? 1 : 0 }}
                              />
                           </ListItemButton>
                        </ListItem>
                     </div>
                  </Tooltip>
               </Link>
            </List>
            <List style={{ paddingTop: 0, paddingBottom: 0 }}>
               <Link
                  to="/support/usage-metrics"
                  style={{ textDecoration: "none", color: "white" }}
               >
                  <Tooltip title={!open ? "Usage & Metrics" : ""} placement="right" arrow>
                     <div
                        className={`list-group-items ${activeNav === "usage-metrics" ? "active" : ""}`}
                     >
                        <ListItem disablePadding sx={{ display: "block" }}>
                           <ListItemButton
                              sx={{
                                 minHeight: 48,
                                 justifyContent: open ? "initial" : "center",
                                 px: 2.5,
                              }}
                           >
                              <ListItemIcon
                                 sx={{
                                    minWidth: 0,
                                    mr: open ? 3 : "auto",
                                    justifyContent: "center",
                                 }}
                              >
                                 <InsightsIcon
                                    className={`svg-icons ${activeNav === "usage-metrics" ? "active" : ""}`}
                                    sx={{ color: "white" }}
                                 />
                              </ListItemIcon>
                              <ListItemText
                                 primary="Usage & Metrics"
                                 sx={{ opacity: open ? 1 : 0 }}
                              />
                           </ListItemButton>
                        </ListItem>
                     </div>
                  </Tooltip>
               </Link>
            </List>
         </Drawer>
      </div>
   );
};
